import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { ImagesEntity, PreviewImage } from '@galabid/gb-dashboard/shared/data-access-images'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { TranslateModule } from '@ngx-translate/core'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import * as ImagesActions from 'libs/gb-dashboard/shared/data-access-images/src/lib/+state/images.actions'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import {
  GbPresenterItemFilterDialogComponent,
  GbPresenterItemFilterDialogResult,
} from '../gb-presenter-item-filter-dialog/gb-presenter-item-filter-dialog.component'
import { GbPresenterExcludedItemsDialogComponent } from '../gb-presenter-excluded-items-dialog/gb-presenter-excluded-items-dialog.component'

@Component({
  selector: 'gb-presenter-tools-thermometer',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
    GbDashboardSharedUiModule,
  ],
  templateUrl: './gb-presenter-tools-thermometer.component.html',
  styleUrl: './gb-presenter-tools-thermometer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsThermometerComponent {
  @Input() toolsFormGroup: FormGroup = new FormGroup({
    fundraising_target: new FormControl(1000),
    track_pledges_only: new FormControl(false),
    base_total_raised_enabled: new FormControl(false),
    thermometer_background_color: new FormControl('#ffffff'),
    thermometer_border_color: new FormControl('#263238'),
    thermometer_color: new FormControl('#F44336'),
    custom_image: new FormControl(null),
  })

  private readonly presenterContextService = inject(PresenterContextService)

  constructor(
    private presenterWidgetsFacade: PresenterWidgetsFacade,
    private gbDashboardDialogService: GbDashboardDialogService,
  ) {}

  get isTemplateMode(): boolean {
    return this.presenterContextService.isTemplateMode
  }

  get fundraisingTargetControl(): FormControl {
    return this.toolsFormGroup.get('fundraising_target') as FormControl
  }

  get trackPledgesOnlyControl(): FormControl {
    return this.toolsFormGroup.get('track_pledges_only') as FormControl
  }

  get baseTotalRaisedEnabledControl(): FormControl {
    return this.toolsFormGroup.get('base_total_raised_enabled') as FormControl
  }

  get thermometerBackgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('thermometer_background_color') as FormControl
  }

  get thermometerBorderColorControl(): FormControl {
    return this.toolsFormGroup.get('thermometer_border_color') as FormControl
  }

  get thermometerColorControl(): FormControl {
    return this.toolsFormGroup.get('thermometer_color') as FormControl
  }

  get customImageControl(): FormControl {
    return this.toolsFormGroup.get('custom_image') as FormControl
  }

  get fontFamilyControl(): FormControl {
    return this.toolsFormGroup.get('font_family') as FormControl
  }

  get fontWeightControl(): FormControl {
    return this.toolsFormGroup.get('font_weight') as FormControl
  }

  get fontSizeControl(): FormControl {
    return this.toolsFormGroup.get('font_size') as FormControl
  }

  get colorControl(): FormControl {
    return this.toolsFormGroup.get('color') as FormControl
  }

  get textAlignControl(): FormControl {
    return this.toolsFormGroup.get('text_align') as FormControl
  }

  get customImages(): ImagesEntity[] {
    const image = this.customImageControl.value as ImagesEntity | null
    return image ? [image] : []
  }

  openCustomImageUploadDialog(): void {
    const isTemplateMode = this.presenterContextService.isTemplateMode
    const templateUuid = this.presenterContextService.templateUuid

    this.presenterWidgetsFacade.dispatch(
      ImagesActions.openImageUploadDialog({
        config: {
          isMulti: false,
          ...(isTemplateMode && templateUuid
            ? {
                uploadTarget: 'projectorTemplate' as const,
                templateUuid,
              }
            : undefined),
        },
        onSuccess: (images: PreviewImage[] | null | undefined) => {
          const image = images?.[0]?.image ?? null
          this.customImageControl.setValue(image)
        },
      }),
    )
  }

  get hiddenItemsControl(): FormControl {
    return this.toolsFormGroup.get('hidden_items') as FormControl
  }

  openFilterItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    const dialogRef = this.gbDashboardDialogService.openDialog(GbPresenterItemFilterDialogComponent, {
      width: '750px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })

    dialogRef.afterClosed().subscribe((result: GbPresenterItemFilterDialogResult | undefined) => {
      if (!result) return

      this.hiddenItemsControl.setValue(result.hiddenItemUuids)
      this.hiddenItemsControl.markAsDirty()
      this.toolsFormGroup.markAsDirty()
    })
  }

  get excludedItemsCount(): number {
    return ((this.hiddenItemsControl?.value || []) as string[]).length
  }

  openExcludedItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    this.gbDashboardDialogService.openDialog(GbPresenterExcludedItemsDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })
  }

  clearCustomImage(): void {
    this.customImageControl.setValue(null)
  }
}
